<template>
  <transition name="push-banner-slide">
    <div v-if="isVisible" class="push-optin-banner" :class="{ behind: stackIndex > 0 }">
      <div class="banner-text">
        <h4 class="banner-title">{{ $t("PushBanner.title") }}</h4>
        <p class="banner-subtitle">{{ $t("PushBanner.subTitle") }}</p>
      </div>
      <div class="banner-actions">
        <v-btn
          class="banner-accept-btn"
          color="#bd9478"
          size="small"
          height="34"
          rounded="lg"
          :loading="loading"
          @click="accept"
        >
          <span>{{ $t("PushBanner.accept") }}</span>
        </v-btn>
        <v-btn
          class="banner-later-btn"
          variant="text"
          size="small"
          height="28"
          @click="dismiss"
        >
          <span>{{ $t("PushBanner.later") }}</span>
        </v-btn>
      </div>
    </div>
  </transition>
</template>

<script>
import PushService from "../../services/PushService";
import {
  registerBanner,
  unregisterBanner,
  bannerIndex,
} from "../../utils/bannerStack";

const BANNER_NAME = "push-opt-in";
const DISMISS_KEY = "pushBannerDismissedAt";
// Re-show the banner this long after the user dismisses it (7 days).
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;

export default {
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    stackIndex() {
      return bannerIndex(BANNER_NAME);
    },
    isVisible() {
      return this.stackIndex !== -1;
    },
  },
  async mounted() {
    if (!PushService.isPushSupported()) return;
    if (Notification.permission !== "default") return;
    if (this.isRecentlyDismissed()) return;
    // No service worker -> no push (e.g. dev server); skip silently.
    if (!(await PushService.hasServiceWorkerRegistration())) return;
    registerBanner(BANNER_NAME, 1); // front card, PWA install banner behind
  },
  unmounted() {
    unregisterBanner(BANNER_NAME);
  },
  methods: {
    isRecentlyDismissed() {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
      if (!dismissedAt) return false;
      return Date.now() - dismissedAt < DISMISS_DURATION;
    },
    async accept() {
      // Slide away right away so the next banner promotes while the native
      // permission prompt is up.
      unregisterBanner(BANNER_NAME);
      try {
        const result = await Notification.requestPermission();
        if (result === "granted") {
          await PushService.subscribeToPush();
        }
      } catch (err) {
        console.log(err);
      }
    },
    dismiss() {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      unregisterBanner(BANNER_NAME);
    },
  },
};
</script>

<style scoped lang="scss">
.push-optin-banner {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 11;
  display: flex;
  align-items: center;
  gap: 14px;
  width: calc(100% - 32px);
  max-width: 520px;
  padding: 14px 16px;
  background: #ffffffee;
  border: 1px solid #ececec;
  border-radius: 16px;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.15);
  transition: transform 0.35s ease, opacity 0.35s ease;

  // Card-deck look: the non-front banner peeks out behind the front one
  &.behind {
    transform: translateX(-50%) translateY(-14px) scale(0.96);
    z-index: 10;
    pointer-events: none;
  }

  .banner-text {
    flex: 1;
    min-width: 0;

    .banner-title {
      font-size: 18px;
      font-weight: 600;
      color: $blackColor;
      margin: 0;
    }

    .banner-subtitle {
      font-size: 14px;
      color: #7c7c7c;
      margin: 2px 0 0 0;
    }
  }

  .banner-actions {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
  }

  .banner-accept-btn {
    color: #ffffff !important;
    font-size: 14px;
  }

  .banner-later-btn {
    color: #9e9e9e !important;
    font-size: 13px;
    min-width: unset;
  }
}

// Slide-up enter / leave transition
.push-banner-slide-enter-active,
.push-banner-slide-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}
.push-banner-slide-enter-from,
.push-banner-slide-leave-to {
  transform: translate(-50%, 120%);
  opacity: 0;
}

@media (max-width: 601px) {
  .push-optin-banner {
    gap: 10px;
    padding: 10px 12px;

    .banner-text .banner-title {
      font-size: 16px;
    }
    .banner-text .banner-subtitle {
      font-size: 12px;
    }
  }
}
</style>
